import { useEffect, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query"

import type {
  TransportOperationName,
  TransportOperationResponse,
  TransportOperationVersionNumber,
} from "@/types_api/backend/transport/operations"

import type { TransportRequestError } from "../utils/transportClient"
import { reportTransportError } from "../utils/transportErrorReporting"
import {
  executeTransportOperation,
  resolveTransportOperationName,
} from "./transportOperationRuntime"
import { resolveTransportNextPageParam } from "./transportPagination"
import { getTransportInfiniteQueryKey } from "./transportQueryKeys"
import type {
  TransportInfiniteHookParams,
  TransportInfiniteQueryData,
  TransportInfiniteQueryKey,
  TransportResolvedRequest,
  TransportRuntimeInfiniteHookParams,
  TransportVersionedInfiniteHookParams,
  TransportVersionedOperationGroup,
  TransportVersionedOperationName,
} from "./transportQueryTypes"
import { useTransportOperationContext } from "./useTransportOperationContext"

export function useTransportInfiniteQuery<Name extends TransportOperationName>(
  params: TransportInfiniteHookParams<Name>,
): UseInfiniteQueryResult<
  TransportInfiniteQueryData<Name>,
  TransportRequestError
>

export function useTransportInfiniteQuery<
  Group extends TransportVersionedOperationGroup,
  Version extends TransportOperationVersionNumber<Group>,
  Name extends TransportVersionedOperationName<Group, Version>,
>(
  params: TransportVersionedInfiniteHookParams<Group, Version>,
): UseInfiniteQueryResult<
  TransportInfiniteQueryData<Name>,
  TransportRequestError
>

export function useTransportInfiniteQuery<Name extends TransportOperationName>(
  params: unknown,
): UseInfiniteQueryResult<
  TransportInfiniteQueryData<Name>,
  TransportRequestError
> {
  const runtimeParams = params as TransportRuntimeInfiniteHookParams<Name>
  const { getNextRequest, options } = runtimeParams
  const operation = resolveTransportOperationName(runtimeParams)
  const {
    reportErrors = true,
    abortOnUnmount = false,
    ...queryOptions
  } = options ?? {}
  const request = "request" in runtimeParams ? runtimeParams.request : undefined
  const requestReady = !("request" in runtimeParams) || request != null
  const typedOperation = operation as Name
  const typedRequest = request
  const {
    getClient,
    isReady,
    organizationSlug,
    queryOrganizationSlug,
    queryWorkspaceId,
  } = useTransportOperationContext(operation)

  const queryKey = useMemo(
    () =>
      getTransportInfiniteQueryKey(
        typedOperation,
        typedRequest,
        queryOrganizationSlug,
        queryWorkspaceId,
      ),
    [queryOrganizationSlug, queryWorkspaceId, typedOperation, typedRequest],
  )

  const enabled = (options?.enabled ?? true) && isReady && requestReady

  const query = useInfiniteQuery<
    TransportOperationResponse<Name>,
    TransportRequestError,
    TransportInfiniteQueryData<Name>,
    TransportInfiniteQueryKey<Name>,
    TransportResolvedRequest<Name> | undefined
  >({
    ...CACHE_PROFILES.standard,
    ...queryOptions,
    enabled,
    initialPageParam: typedRequest ?? undefined,
    queryKey,
    queryFn: (context) => {
      const { pageParam } = context
      if (!requestReady) {
        return Promise.resolve(
          null as unknown as TransportOperationResponse<Name>,
        )
      }

      return executeTransportOperation(
        getClient(),
        typedOperation,
        pageParam as TransportResolvedRequest<Name> | undefined,
        abortOnUnmount ? { signal: context.signal } : undefined,
      )
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      resolveTransportNextPageParam(
        lastPage,
        allPages,
        lastPageParam,
        getNextRequest,
      ),
  })

  useEffect(() => {
    if (reportErrors && query.error) {
      reportTransportError({
        error: query.error,
        kind: "infinite_query",
        operation,
        organizationSlug,
        errorUpdatedAt: query.errorUpdatedAt,
      })
    }
  }, [
    operation,
    organizationSlug,
    query.error,
    query.errorUpdatedAt,
    reportErrors,
  ])

  return query
}
